import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useBlocker, Link } from "react-router-dom";
import { API_BASE, formatApiErrorDetail, describeApiError } from "@/lib/api";
// PHASE 9.2 — deferred per user request 2026-06-29.
// Smart Folder Drop + Folder Memory are inactive; helpers live in @/lib/idb
// and backend POST /api/videos/fingerprint-check stays in place for future reuse.
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import {
  UploadCloud,
  X,
  Globe,
  Lock,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Lightbulb,
  Film,
  Ban,
  Pause,
  Play,
  Wifi,
  WifiOff,
} from "lucide-react";

// Mirrors backend regex ^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;
const MAX_FILES = 20;
const MAX_FILE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB
const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MiB chunks (≈20 s on 3.96 Mbit/s DSL)
const LS_PREFIX = "360k:upload:"; // one localStorage key per fingerprint
const LS_VERSION = 1;

// ─────────────────────────────── Helpers ───────────────────────────────────
function fmtSize(n) {
  if (!n) return "";
  const mb = n / 1024 / 1024;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function defaultTitleFromFilename(name) {
  if (!name) return "";
  return name.replace(/\.mp4$/i, "").replace(/[_-]+/g, " ").trim();
}

function newItemId() {
  return (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    ? crypto.randomUUID()
    : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sanitizeSlug(input) {
  const map = { ä: "ae", ö: "oe", ü: "ue", ß: "ss", Ä: "ae", Ö: "oe", Ü: "ue" };
  return String(input || "")
    .replace(/[äöüÄÖÜß]/g, (c) => map[c] || c)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

// File fingerprint for resume detection. Uses name+size+lastModified — no need
// to hash content because the user picks the file manually.
function fileFingerprint(file) {
  const lm = file.lastModified || 0;
  return `${file.name}|${file.size}|${lm}`;
}

function readLS(fingerprint) {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + fingerprint);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== LS_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLS(fingerprint, payload) {
  try {
    window.localStorage.setItem(
      LS_PREFIX + fingerprint,
      JSON.stringify({ ...payload, version: LS_VERSION }),
    );
  } catch (e) {
    // Storage quota exceeded → drop oldest entries. Best-effort, not critical.
    if (e?.name === "QuotaExceededError") {
      for (const k of Object.keys(window.localStorage)) {
        if (k.startsWith(LS_PREFIX)) window.localStorage.removeItem(k);
      }
      try {
        window.localStorage.setItem(
          LS_PREFIX + fingerprint,
          JSON.stringify({ ...payload, version: LS_VERSION }),
        );
      } catch {
        /* give up */
      }
    }
  }
}

function clearLS(fingerprint) {
  try {
    window.localStorage.removeItem(LS_PREFIX + fingerprint);
  } catch {
    /* noop */
  }
}

function listResumableFromLS() {
  const out = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(LS_PREFIX)) continue;
      try {
        const v = JSON.parse(window.localStorage.getItem(k));
        if (v && v.version === LS_VERSION) {
          out.push({ key: k.slice(LS_PREFIX.length), entry: v });
        }
      } catch {
        /* skip */
      }
    }
  } catch {
    /* noop */
  }
  return out;
}

// Returns enriched info for each LS entry suitable for the resume banner:
// { fingerprint, uploadId, fileName, fileSize, completedCount, totalChunks, progressPct }
function listPendingUploads() {
  return listResumableFromLS().map(({ key, entry }) => {
    const total = Number(entry.totalChunks || 0) || 1;
    const done = (entry.completedParts || []).length;
    return {
      fingerprint: key,
      uploadId: entry.uploadId || null,
      fileName: entry.fileName || key.split("|")[0] || "(unbekannt)",
      fileSize: Number(entry.fileSize || 0),
      fileLastModified: Number(entry.fileLastModified || 0),
      completedCount: done,
      totalChunks: total,
      progressPct: Math.min(100, Math.round((done / total) * 100)),
      title: entry.title || "",
      visibility: entry.visibility || "private",
      slug: entry.slug || "",
      videoId: entry.videoId || null,
      chunkSize: Number(entry.chunkSize || 0),
    };
  });
}

// Match a freshly picked File against the LS list.
// Returns { match: "exact"|"fuzzy"|"none", entry: PendingEntry|null }.
// "exact"  → name + size + lastModified all match
// "fuzzy"  → name + size match, lastModified differs (e.g. file was copied)
// "none"   → no candidate at all
function matchPendingForFile(file, pending) {
  const name = file.name;
  const size = file.size;
  const lm = file.lastModified || 0;
  let fuzzy = null;
  for (const p of pending) {
    if (p.fileName === name && p.fileSize === size) {
      if (p.fileLastModified === lm) return { match: "exact", entry: p };
      if (!fuzzy) fuzzy = p;
    }
  }
  if (fuzzy) return { match: "fuzzy", entry: fuzzy };
  return { match: "none", entry: null };
}

function statusBadge(status) {
  switch (status) {
    case "queued":
      return { label: "Wartend", cls: "bg-[#1f1f23] text-[#9a9aa3] border-[#2a2a30]" };
    case "initializing":
      return { label: "Starte", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" };
    case "uploading":
      return { label: "Lädt", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" };
    case "paused":
      return { label: "Pausiert", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" };
    case "completing":
      return { label: "Finalisiert", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" };
    case "finalizing":
      return { label: "Wird finalisiert…", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" };
    case "done":
      return { label: "Fertig", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
    case "failed":
      return { label: "Fehler", cls: "bg-red-500/10 text-red-300 border-red-500/30" };
    case "cancelled":
      return { label: "Abgebrochen", cls: "bg-[#1f1f23] text-[#9a9aa3] border-[#2a2a30]" };
    default:
      return { label: status, cls: "bg-[#1f1f23] text-[#9a9aa3] border-[#2a2a30]" };
  }
}

// ─────────────────────────────── ResolutionTip (unchanged) ─────────────────
function ResolutionTip() {
  return (
    <div
      className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e] p-4 sm:p-5 mb-6"
      data-testid="upload-resolution-tip"
    >
      <div className="flex items-start gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#ff6b35]/10 text-[#ff6b35] shrink-0">
          <Lightbulb size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white">Tipp für schnelleren Upload</h3>
          <p className="text-xs text-[#9a9aa3] mt-1 leading-relaxed">
            Du musst nicht in voller Auflösung exportieren. Empfehlung aus Insta360 Studio:
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs">
            <div className="flex justify-between sm:block">
              <dt className="text-[#6a6a73]">Auflösung</dt>
              <dd className="text-white tabular-nums sm:mt-0.5">3840 × 1920</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-[#6a6a73]">Bitrate</dt>
              <dd className="text-white tabular-nums sm:mt-0.5">~30 Mbps</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-[#6a6a73]">Dateigröße</dt>
              <dd className="text-white sm:mt-0.5">ca. 3× kleiner als 5760×2880</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-[#6a6a73]">Qualität</dt>
              <dd className="text-emerald-300 sm:mt-0.5">bleibt gleich</dd>
            </div>
          </dl>
          <p className="text-xs text-[#6a6a73] mt-3 leading-relaxed">
            Resume-Upload aktiv — bei Unterbrechung (WLAN-Drop, Reload) machen wir automatisch dort weiter, wo wir aufgehört haben.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── Queue row ─────────────────────────────────
function QueueRow({ item, allowCustomSlug, onTitleChange, onPause, onResume, onCancel, onRemove }) {
  const badge = statusBadge(item.status);
  const isUploading = item.status === "uploading" || item.status === "initializing";
  const isPaused = item.status === "paused";
  const isCompleting = item.status === "completing";
  const isDone = item.status === "done";
  const isFailed = item.status === "failed";
  const titleLocked = ["uploading", "initializing", "completing", "done"].includes(item.status);
  const showPause = isUploading;
  const showResume = isPaused || isFailed;
  const showCancel = isUploading || isPaused || isCompleting;
  const showRemove = !showCancel;

  return (
    <div
      className="rounded-xl border border-[#2a2a30] bg-[#0d0d0e] p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:gap-4 items-center"
      data-testid={`upload-queue-row-${item.id}`}
    >
      <div className="min-w-0 flex items-start gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#17171a] shrink-0">
          {isDone ? (
            <CheckCircle2 size={16} className="text-emerald-400" />
          ) : isFailed ? (
            <AlertTriangle size={16} className="text-red-400" />
          ) : isPaused ? (
            <Pause size={16} className="text-amber-300" />
          ) : item.status === "cancelled" ? (
            <Ban size={16} className="text-[#9a9aa3]" />
          ) : (
            <Film size={16} className="text-[#9a9aa3]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${badge.cls}`}
              data-testid={`upload-queue-status-${item.id}`}
            >
              {(isUploading || isCompleting) && <Loader2 size={10} className="animate-spin mr-1" />}
              {badge.label}
              {(isUploading || isPaused) && (
                <span className="ml-1.5 tabular-nums text-[#9a9aa3]">{item.progress}%</span>
              )}
            </span>
            {item.resumed && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                Fortgesetzt
              </span>
            )}
            <span className="text-[11px] text-[#6a6a73] truncate" title={item.file.name}>
              {item.file.name} · {fmtSize(item.file.size)}
            </span>
          </div>
          <input
            value={item.title}
            onChange={(e) => onTitleChange(item.id, e.target.value)}
            disabled={titleLocked}
            placeholder="Titel des Videos (z. B. Sankt-Peter-Ording, September)"
            data-testid={`upload-queue-title-input-${item.id}`}
            className="w-full h-9 px-3 rounded-md bg-[#17171a] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-sm text-white placeholder:text-[#6a6a73] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {(isUploading || isPaused || isCompleting) && (
            <div className="mt-2 h-1.5 rounded-full bg-[#1f1f23] overflow-hidden">
              <div
                className="h-1.5 rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] transition-[width] duration-200"
                style={{ width: `${item.progress}%` }}
                data-testid={`upload-queue-progress-${item.id}`}
              />
            </div>
          )}
          {item.errorMessage && (
            <p
              className="mt-1.5 text-[11px] text-red-300"
              data-testid={`upload-queue-error-${item.id}`}
            >
              {item.errorMessage}
            </p>
          )}
          {!allowCustomSlug && isDone && item.responseSlug && (
            <p className="mt-1.5 text-[11px] text-[#9a9aa3]">
              Link: <span className="text-white font-mono">/v/{item.responseSlug}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex sm:flex-col items-end gap-1.5 shrink-0">
        {showPause && (
          <button
            type="button"
            onClick={() => onPause(item.id)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-[#1f1f23] hover:bg-[#2a2a30] text-white text-xs font-semibold transition-colors"
            data-testid={`upload-queue-pause-${item.id}`}
          >
            <Pause size={13} />
            Pause
          </button>
        )}
        {showResume && (
          <button
            type="button"
            onClick={() => onResume(item.id)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
            data-testid={`upload-queue-resume-${item.id}`}
          >
            <Play size={13} />
            Fortsetzen
          </button>
        )}
        {showCancel && (
          <button
            type="button"
            onClick={() => onCancel(item.id)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold shadow-[0_4px_14px_rgba(239,68,68,0.3)] transition-colors"
            data-testid={`upload-queue-cancel-${item.id}`}
          >
            <X size={13} />
            Abbrechen
          </button>
        )}
        {showRemove && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label="Aus Liste entfernen"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
            data-testid={`upload-queue-remove-${item.id}`}
          >
            <X size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────── Main component ────────────────────────────
export default function AdminUpload() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [slugInput, setSlugInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [orphanCount, setOrphanCount] = useState(0); // legacy alias kept stable
  const [pendingUploads, setPendingUploads] = useState([]); // full LS entries
  const refreshPending = useCallback(() => {
    const list = listPendingUploads();
    setPendingUploads(list);
    setOrphanCount(list.length);
    return list;
  }, []);

  // ─────────────── Folder Memory + Smart Drop (Phase 9.2) ───────────────
  // PHASE 9.2 — deferred per user request 2026-06-29.
  // State, handlers and FileSystem-Access auto-resume are intentionally removed
  // here. The backend endpoint /api/videos/fingerprint-check and the IndexedDB
  // helper in @/lib/idb.js are kept for future reactivation.

  const queueRef = useRef(queue);
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Per-item AbortController for the active in-flight fetch.
  const abortMapRef = useRef(new Map());
  // Per-item pause flag (set true to stop the chunk loop at next iteration).
  const pauseMapRef = useRef(new Map());
  const inputRef = useRef(null);

  // Track localStorage entries that haven't yet been matched to a picked file
  // (so we can show a resume banner).
  useEffect(() => {
    refreshPending();
  }, [refreshPending]);

  // ─────────────── Folder Memory: load + permission + auto-resume ───────────
  // PHASE 9.2 — deferred per user request 2026-06-29.
  // The onPickFolder/onClearFolder/onRequestFolderPermission handlers and the
  // useEffect-based directory-handle restore + auto-resume have been removed.
  // Restore from git history when reactivating.

  // ──────────────── Queue mutator with SYNCHRONOUS ref update ────────────────
  // Approach A (per iteration_14.json RCA): We compute `next` from the
  // CURRENT `queueRef.current` (source of truth) and assign the ref BEFORE
  // scheduling the React state commit. This guarantees that any code reading
  // `queueRef.current` immediately after `updateItem(..., 'done')` — including
  // the microtask-scheduled `runScheduler` — sees the fresh state and
  // correctly kicks the next PENDING upload. Without this sync, React 18's
  // batched-updater timing left `queueRef.current` stale until React
  // committed, so the scheduler bailed out (saw item 1 still as
  // `completing`) and the batch queue deadlocked after file #1.
  const updateQueue = (updater) => {
    const prev = queueRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    queueRef.current = next;          // SYNC: ref now reflects truth
    setQueue(next);                   // React commit follows separately
  };

  // Ref-pattern for runScheduler — declared AFTER kickoffUpload but referenced
  // inside it. The ref lets us point to the latest runScheduler without
  // creating a temporal dead zone in kickoffUpload's dep array.
  const runSchedulerRef = useRef(null);
  const safeRunScheduler = useCallback(() => {
    runSchedulerRef.current?.();
  }, []);
  // React 18 batches the setState from `updateItem(..., status: "done")` and
  // schedules the updater to run on a later microtask. When kickoffUpload's
  // `finally` block called `safeRunScheduler()` synchronously, `queueRef.current`
  // still contained the finishing item as `status: "completing"` → the scheduler
  // saw an `active` item and bailed → the next PENDING upload never started
  // (the "queue hangs after 1st done" bug). Deferring the scheduler kick to a
  // microtask lets React's committed state land BEFORE the scheduler reads it.
  const safeRunSchedulerDeferred = useCallback(() => {
    queueMicrotask(() => runSchedulerRef.current?.());
  }, []);

  const updateItem = useCallback((id, patch) => {
    updateQueue((q) =>
      q.map((it) => (it.id === id ? { ...it, ...(typeof patch === "function" ? patch(it) : patch) } : it)),
    );
  }, []);

  // ──────────────── Online/offline auto-resume ────────────────
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      toast.success("Verbindung wieder da — Uploads werden fortgesetzt.");
      // Auto-resume paused uploads.
      queueRef.current
        .filter((it) => it.status === "paused" && it.autoPaused)
        .forEach((it) => kickoffUpload(it.id, { auto: true }));
    };
    const onOffline = () => {
      setOnline(false);
      toast.error("Offline — laufende Uploads werden pausiert.");
      // Auto-pause uploading items.
      queueRef.current
        .filter((it) => it.status === "uploading" || it.status === "initializing")
        .forEach((it) => pauseItemInternal(it.id, { auto: true }));
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────────────── File intake ────────────────
  // Direct entry point from the dropzone and file picker.
  // PHASE 9.2 (Smart Folder Drop + 3-category classification) is deferred —
  // we hand straight off to commitToQueue, which already does MP4/size
  // validation AND per-file localStorage resume matching.
  const addFiles = (filesList) => {
    commitToQueue(filesList);
  };

  // ──────────────── File intake with auto-matching ────────────────
  // commitToQueue: takes already-classified files and pushes them into the
  // upload queue, picking up resume state from localStorage if present.
  // Auto-starts uploads for matched (resumed) items so the user doesn't
  // have to press the submit button.
  const commitToQueue = (filesList) => {
    setGlobalError("");
    const incoming = Array.from(filesList || []);
    if (incoming.length === 0) return;
    const accepted = [];
    const rejected = [];
    for (const f of incoming) {
      if (f.type !== "video/mp4" && !/\.mp4$/i.test(f.name)) {
        rejected.push(`${f.name}: nur MP4 erlaubt`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`${f.name}: größer als 5 GB`);
        continue;
      }
      accepted.push(f);
    }
    if (rejected.length) {
      toast.error(rejected.length === 1 ? rejected[0] : `${rejected.length} Dateien übersprungen`);
    }
    if (!accepted.length) return;

    // Compute matches BEFORE touching state so we can show a single confirm
    // for any fuzzy (size matches, lastModified differs) candidates.
    const pendingList = listPendingUploads();
    const matchedFingerprints = new Set();
    const resolved = accepted.map((file) => {
      const exactFp = fileFingerprint(file);
      const exactSaved = readLS(exactFp);
      if (exactSaved) {
        matchedFingerprints.add(exactFp);
        return { file, saved: exactSaved, matchKind: "exact", overrideFp: null };
      }
      const fz = matchPendingForFile(file, pendingList);
      if (fz.match === "fuzzy") {
        // Bug: a different file could happen to have same name+size but the
        // user just didn't realise. Ask once to be safe.
        const ok = window.confirm(
          `Datei „${file.name}" hat dieselbe Größe wie ein pausierter Upload, ` +
            `aber ein anderes Änderungsdatum. ` +
            `Trotzdem als Fortsetzung dieses Uploads behandeln?`,
        );
        if (ok) {
          matchedFingerprints.add(fz.entry.fingerprint);
          // Use the saved entry under the original fingerprint key, but
          // RE-KEY the LS to the new fingerprint so future reloads match
          // immediately without another prompt.
          const newFp = fileFingerprint(file);
          const savedEntry = readLS(fz.entry.fingerprint);
          if (savedEntry) {
            writeLS(newFp, savedEntry);
            clearLS(fz.entry.fingerprint);
          }
          return { file, saved: savedEntry, matchKind: "fuzzy", overrideFp: newFp };
        }
      }
      return { file, saved: null, matchKind: "none", overrideFp: null };
    });

    updateQueue((q) => {
      const room = MAX_FILES - q.length;
      if (room <= 0) {
        toast.error(`Maximal ${MAX_FILES} Dateien gleichzeitig.`);
        return q;
      }
      const slice = resolved.slice(0, room);
      if (resolved.length > slice.length) {
        toast.error(`Maximal ${MAX_FILES} Dateien — ${resolved.length - slice.length} übersprungen.`);
      }
      return [
        ...q,
        ...slice.map(({ file, saved, matchKind, overrideFp }) => {
          const fp = overrideFp || fileFingerprint(file);
          const total = saved?.totalChunks
            || Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
          return {
            id: newItemId(),
            file,
            fingerprint: fp,
            title: saved?.title || "",
            status: "queued",
            progress: saved
              ? Math.round(((saved.completedParts || []).length / total) * 100)
              : 0,
            errorMessage: "",
            responseSlug: "",
            resumed: !!saved,
            matchKind, // "exact" | "fuzzy" | "none"
            autoPaused: false,
            uploadId: saved?.uploadId || null,
            videoId: saved?.videoId || null,
            totalChunks: total,
            chunkSize: saved?.chunkSize || CHUNK_SIZE,
            completedParts: saved?.completedParts || [],
          };
        }),
      ];
    });

    // Banner now only shows unmatched pending entries.
    setPendingUploads((prev) => prev.filter((p) => !matchedFingerprints.has(p.fingerprint)));
    setOrphanCount((c) => Math.max(0, c - matchedFingerprints.size));

    // Auto-start resumed uploads. New (unmatched) files wait for the submit button
    // so the user can still set visibility/slug.
    const resumedCount = resolved.filter((r) => r.saved).length;
    if (resumedCount > 0) {
      setHasSubmitted(true);
      queueMicrotask(() => {
        const q = queueRef.current;
        const resumedItems = q.filter((it) => it.resumed && it.status === "queued");
        for (const it of resumedItems) {
          // Mark as pending so the scheduler will pick it up one after the other.
          updateItem(it.id, { status: "pending" });
        }
        safeRunScheduler();
      });
      toast.success(
        resumedCount === 1
          ? "Upload wird fortgesetzt"
          : `${resumedCount} Uploads werden fortgesetzt`,
      );
    }
  };

  const onPickFiles = (e) => {
    addFiles(e.target.files);
    if (e.target) e.target.value = "";
  };

  // ──────────────── Per-item operations ────────────────
  const onTitleChange = (id, value) => updateItem(id, { title: value });

  const onRemoveItem = (id) => {
    const it = queueRef.current.find((x) => x.id === id);
    if (it && it.fingerprint) clearLS(it.fingerprint);
    updateQueue((q) => q.filter((it2) => it2.id !== id));
    refreshPending();
  };

  const pauseItemInternal = (id, { auto = false } = {}) => {
    pauseMapRef.current.set(id, true);
    const ctrl = abortMapRef.current.get(id);
    if (ctrl) {
      try {
        ctrl.abort();
      } catch {
        /* noop */
      }
    }
    updateItem(id, { status: "paused", autoPaused: auto });
  };

  const onPauseItem = (id) => pauseItemInternal(id, { auto: false });

  const onResumeItem = (id) => {
    pauseMapRef.current.delete(id);
    updateItem(id, { status: "queued", errorMessage: "", autoPaused: false });
    kickoffUpload(id, { auto: false });
  };

  const onCancelItem = async (id) => {
    pauseMapRef.current.set(id, true);
    const ctrl = abortMapRef.current.get(id);
    if (ctrl) {
      try {
        ctrl.abort();
      } catch {
        /* noop */
      }
    }
    const item = queueRef.current.find((it) => it.id === id);
    updateItem(id, { status: "cancelled", autoPaused: false });
    if (item?.uploadId) {
      try {
        await fetch(`${API_BASE}/uploads/${encodeURIComponent(item.uploadId)}`, {
          method: "DELETE",
          credentials: "include",
        });
      } catch {
        /* best effort */
      }
    }
    if (item?.fingerprint) clearLS(item.fingerprint);
    refreshPending();
    pauseMapRef.current.delete(id);
    abortMapRef.current.delete(id);
    // The next queued item (if any) can now go.
    runScheduler();
  };

  // ──────────────── Backend helpers ────────────────
  const initUpload = async (item) => {
    const body = {
      filename: item.file.name,
      file_size: item.file.size,
      chunk_size: item.chunkSize,
      total_chunks: item.totalChunks,
      title: (item.title || "").trim(),
      description: "",
      visibility,
      slug: queueRef.current.length === 1 && slugInput.trim() ? sanitizeSlug(slugInput) : "",
      content_type: "video/mp4",
    };
    const resp = await fetch(`${API_BASE}/uploads/init`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => ({}));
      throw new Error(formatApiErrorDetail(detail.detail) || `init HTTP ${resp.status}`);
    }
    return resp.json();
  };

  const fetchStatus = async (uploadId) => {
    const resp = await fetch(
      `${API_BASE}/uploads/${encodeURIComponent(uploadId)}`,
      { credentials: "include" },
    );
    if (resp.status === 404) return null;
    if (!resp.ok) throw new Error(`status HTTP ${resp.status}`);
    return resp.json();
  };

  const presignPart = async (uploadId, partNumber, signal) => {
    const resp = await fetch(
      `${API_BASE}/uploads/${encodeURIComponent(uploadId)}/parts/${partNumber}/url`,
      { credentials: "include", signal },
    );
    if (!resp.ok) throw new Error(`presign HTTP ${resp.status}`);
    return (await resp.json()).url;
  };

  const putPartToR2 = async (url, blob, signal) => {
    const resp = await fetch(url, {
      method: "PUT",
      body: blob,
      signal,
    });
    if (!resp.ok) throw new Error(`R2 PUT ${resp.status}`);
    const etag = (resp.headers.get("ETag") || "").replace(/"/g, "");
    if (!etag) throw new Error("R2 ETag fehlt (CORS-Konfiguration?)");
    return etag;
  };

  const confirmPart = async (uploadId, partNumber, etag, size, signal) => {
    const resp = await fetch(
      `${API_BASE}/uploads/${encodeURIComponent(uploadId)}/parts/${partNumber}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ etag, size }),
        signal,
      },
    );
    if (!resp.ok) throw new Error(`confirm HTTP ${resp.status}`);
  };

  const completeUpload = async (uploadId) => {
    const resp = await fetch(
      `${API_BASE}/uploads/${encodeURIComponent(uploadId)}/complete`,
      { method: "POST", credentials: "include" },
    );
    if (!resp.ok) {
      // Phase-11 error-hardening: NEVER swallow the HTTP status into a
      // generic "Etwas ist schiefgelaufen". `describeApiError` puts the
      // endpoint + status + Cloudflare hint into the message so the user
      // (and Prod-triage) knows exactly what failed.
      throw new Error(await describeApiError(resp, "POST /uploads/.../complete"));
    }
    return resp.json();
  };

  // Poll `GET /api/uploads/{id}` until the backend finaliser reports
  // `status="ready"` (returns the video_slug) or `status="failed"`. Uses
  // exponential backoff 1 s → 10 s to stay gentle on the API while a
  // multi-GB R2 CompleteMultipartUpload finishes server-side. Hard cap at
  // 15 min so a genuinely broken finaliser does not leave the UI spinning
  // forever.
  const pollFinalization = async (uploadId, onProgress) => {
    const startedAt = Date.now();
    const HARD_CAP_MS = 15 * 60 * 1000;   // 15 min
    let delayMs = 1000;                    // start at 1 s
    const MAX_DELAY_MS = 10_000;
    while (Date.now() - startedAt < HARD_CAP_MS) {
      await new Promise((r) => setTimeout(r, delayMs));
      let resp;
      try {
        resp = await fetch(
          `${API_BASE}/uploads/${encodeURIComponent(uploadId)}`,
          { method: "GET", credentials: "include" },
        );
      } catch (e) {
        // Transient network blip → keep retrying, don't fail the upload.
        delayMs = Math.min(delayMs * 2, MAX_DELAY_MS);
        continue;
      }
      if (resp.status === 404) {
        throw new Error(
          `Upload-Datensatz nicht mehr gefunden — Backend hat ihn gelöscht bevor die Verarbeitung abgeschlossen war.`,
        );
      }
      if (!resp.ok) {
        // Transient server error (Cloudflare 520 etc.) → back off + retry.
        delayMs = Math.min(delayMs * 2, MAX_DELAY_MS);
        onProgress?.({ status: "finalizing", transient: true });
        continue;
      }
      let data;
      try {
        data = await resp.json();
      } catch {
        delayMs = Math.min(delayMs * 2, MAX_DELAY_MS);
        continue;
      }
      if (data.status === "ready") {
        return data;   // has video_slug
      }
      if (data.status === "failed") {
        throw new Error(
          data.error ||
            "Finalisierung im Backend fehlgeschlagen (kein Detail geliefert)",
        );
      }
      // Still "uploading" or "finalizing" — keep polling.
      onProgress?.(data);
      delayMs = Math.min(delayMs * 2, MAX_DELAY_MS);
    }
    throw new Error(
      "Backend braucht länger als 15 Min. für die Finalisierung. " +
        "Prüfe die Bibliothek — das Video kann trotzdem gleich auftauchen.",
    );
  };

  // ──────────────── The chunked upload loop ────────────────
  const kickoffUpload = useCallback(async (itemId, { auto = false } = {}) => {
    const item = queueRef.current.find((it) => it.id === itemId);
    if (!item) return;
    if (!navigator.onLine) {
      updateItem(itemId, { status: "paused", autoPaused: true, errorMessage: "" });
      return;
    }
    pauseMapRef.current.delete(itemId);

    // Phase 1: ensure we have an upload_id.
    let uploadId = item.uploadId;
    let totalChunks = item.totalChunks;
    let chunkSize = item.chunkSize;
    let completed = new Set(item.completedParts);

    if (!uploadId) {
      updateItem(itemId, { status: "initializing", errorMessage: "", lastProgressAt: Date.now() });
      try {
        const init = await initUpload(item);
        uploadId = init.upload_id;
        totalChunks = init.total_chunks;
        chunkSize = init.chunk_size;
        completed = new Set();
        writeLS(item.fingerprint, {
          uploadId,
          videoId: init.video_id,
          fileName: item.file.name,
          fileSize: item.file.size,
          fileLastModified: item.file.lastModified || 0,
          chunkSize,
          totalChunks,
          completedParts: [],
          title: item.title,
          visibility,
          slug: queueRef.current.length === 1 ? sanitizeSlug(slugInput) : "",
          createdAt: new Date().toISOString(),
        });
        updateItem(itemId, {
          uploadId,
          videoId: init.video_id,
          totalChunks,
          chunkSize,
        });
      } catch (err) {
        updateItem(itemId, {
          status: "failed",
          errorMessage: err.message || "Upload-Start fehlgeschlagen",
          autoPaused: false,
        });
        safeRunScheduler();
        return;
      }
    } else {
      // Resume: refresh server state so we know exactly which parts are done.
      try {
        const status = await fetchStatus(uploadId);
        if (!status) {
          // Server discarded the upload (24h stale cleanup). Wipe & restart.
          clearLS(item.fingerprint);
          updateItem(itemId, {
            uploadId: null,
            completedParts: [],
            resumed: false,
            errorMessage: "",
          });
          kickoffUpload(itemId, { auto });
          return;
        }
        totalChunks = status.total_chunks;
        chunkSize = status.chunk_size;
        completed = new Set((status.completed_parts || []).map((p) => p.part_number));
        updateItem(itemId, {
          totalChunks,
          chunkSize,
          completedParts: Array.from(completed),
        });
        writeLS(item.fingerprint, {
          uploadId,
          videoId: item.videoId,
          fileName: item.file.name,
          fileSize: item.file.size,
          fileLastModified: item.file.lastModified || 0,
          chunkSize,
          totalChunks,
          completedParts: Array.from(completed),
          title: item.title,
          visibility,
          slug: queueRef.current.length === 1 ? sanitizeSlug(slugInput) : "",
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        updateItem(itemId, {
          status: "failed",
          errorMessage: err.message || "Status-Abfrage fehlgeschlagen",
          autoPaused: false,
        });
        safeRunScheduler();
        return;
      }
    }

    // Phase 2: upload remaining chunks SEQUENTIALLY.
    updateItem(itemId, { status: "uploading", errorMessage: "", autoPaused: false, lastProgressAt: Date.now() });

    for (let n = 1; n <= totalChunks; n++) {
      if (pauseMapRef.current.get(itemId)) return; // user paused/cancelled
      if (completed.has(n)) {
        const pct = Math.round((completed.size / totalChunks) * 100);
        updateItem(itemId, { progress: pct, lastProgressAt: Date.now() });
        continue;
      }
      const start = (n - 1) * chunkSize;
      const end = Math.min(start + chunkSize, item.file.size);
      const blob = item.file.slice(start, end);

      const ctrl = new AbortController();
      abortMapRef.current.set(itemId, ctrl);

      let presigned;
      try {
        presigned = await presignPart(uploadId, n, ctrl.signal);
      } catch (err) {
        if (err.name === "AbortError" || pauseMapRef.current.get(itemId)) return;
        updateItem(itemId, {
          status: "failed",
          errorMessage: err.message || "Presign fehlgeschlagen",
          autoPaused: false,
        });
        safeRunScheduler();
        return;
      }

      let etag;
      try {
        etag = await putPartToR2(presigned, blob, ctrl.signal);
      } catch (err) {
        if (err.name === "AbortError" || pauseMapRef.current.get(itemId)) return;
        // Network drop in mid-PUT → switch to paused so the user/auto-resume can retry.
        if (!navigator.onLine) {
          updateItem(itemId, { status: "paused", autoPaused: true, errorMessage: "" });
          return;
        }
        updateItem(itemId, {
          status: "failed",
          errorMessage: err.message || "R2-Upload fehlgeschlagen",
          autoPaused: false,
        });
        safeRunScheduler();
        return;
      }

      try {
        await confirmPart(uploadId, n, etag, blob.size, ctrl.signal);
      } catch (err) {
        if (err.name === "AbortError" || pauseMapRef.current.get(itemId)) return;
        updateItem(itemId, {
          status: "failed",
          errorMessage: err.message || "Bestätigung fehlgeschlagen",
          autoPaused: false,
        });
        safeRunScheduler();
        return;
      }

      completed.add(n);
      const pct = Math.round((completed.size / totalChunks) * 100);
      const completedArr = Array.from(completed);
      writeLS(item.fingerprint, {
        uploadId,
        videoId: item.videoId,
        fileName: item.file.name,
        fileSize: item.file.size,
        fileLastModified: item.file.lastModified || 0,
        chunkSize,
        totalChunks,
        completedParts: completedArr,
        title: item.title,
        visibility,
        slug: queueRef.current.length === 1 ? sanitizeSlug(slugInput) : "",
        createdAt: new Date().toISOString(),
      });
      updateItem(itemId, { progress: pct, completedParts: completedArr, lastProgressAt: Date.now() });
    }

    abortMapRef.current.delete(itemId);

    // Phase 3: tell the backend to finalise. This now returns HTTP 202
    // almost immediately — the actual R2 CompleteMultipartUpload + video
    // registration run as a background task on the server. We then poll
    // until the finaliser reports ready or failed. This eliminates the
    // Cloudflare 100 s ingress timeout that used to turn every 2.5+ GB
    // upload into HTTP 520.
    updateItem(itemId, { status: "completing", progress: 100, lastProgressAt: Date.now() });
    try {
      const accepted = await completeUpload(uploadId);
      // Fast path: retry-hit — server saw the upload already had a video row
      // and returned ready immediately.
      if (accepted.status === "ready" && accepted.video_slug) {
        clearLS(item.fingerprint);
        updateItem(itemId, {
          status: "done",
          responseSlug: accepted.video_slug,
          errorMessage: "",
        });
      } else {
        updateItem(itemId, {
          status: "finalizing",
          errorMessage: "",
          lastProgressAt: Date.now(),
        });
        const finalDoc = await pollFinalization(uploadId, () => {
          // Keep the watchdog timestamp warm during long finalisations so
          // the 60 s stuck-detector does not fire on a healthy background
          // finaliser call.
          updateItem(itemId, { lastProgressAt: Date.now() });
        });
        clearLS(item.fingerprint);
        updateItem(itemId, {
          status: "done",
          responseSlug: finalDoc.video_slug || finalDoc.slug,
          errorMessage: "",
        });
      }
    } catch (err) {
      updateItem(itemId, {
        status: "failed",
        errorMessage: err.message || "Finalisieren fehlgeschlagen",
      });
    } finally {
      refreshPending();
      safeRunSchedulerDeferred();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility, slugInput, refreshPending, updateItem, safeRunSchedulerDeferred]);

  // ──────────────── Sequential scheduler (1 file at a time) ────────────────
  const runScheduler = useCallback(() => {
    const q = queueRef.current;
    const active = q.find((it) =>
      ["uploading", "initializing", "completing", "finalizing"].includes(it.status),
    );
    if (active) return;
    // Auto-skip items still 'queued' once submit is pressed.
    const next = q.find((it) => it.status === "pending");
    if (!next) return;
    updateItem(next.id, { status: "queued" }); // visual reset before kickoff
    kickoffUpload(next.id, { auto: false });
  }, [kickoffUpload, updateItem]);
  // Keep the ref in sync so safeRunScheduler() (called from kickoffUpload)
  // dispatches to the latest closure without forcing kickoffUpload to re-create.
  runSchedulerRef.current = runScheduler;

  const onSubmit = (e) => {
    e?.preventDefault?.();
    setGlobalError("");
    const ready = queue.filter((it) => it.status === "queued");
    if (ready.length === 0) {
      setGlobalError("Bitte mindestens eine Datei wählen.");
      return;
    }
    if (queue.length === 1 && slugInput.trim()) {
      const cleaned = sanitizeSlug(slugInput);
      if (!SLUG_RE.test(cleaned)) {
        setGlobalError("Ungültiger Link-Name. a–z, 0–9, Bindestriche, 1–60 Zeichen.");
        return;
      }
    }
    setHasSubmitted(true);
    // Mark all queued items as 'pending' so the scheduler can pick them sequentially.
    updateQueue((q) =>
      q.map((it) => (it.status === "queued" ? { ...it, status: "pending" } : it)),
    );
    queueMicrotask(() => runScheduler());
  };

  // ──────────────── Derived ────────────────
  const inFlight = queue.some((it) =>
    ["uploading", "initializing", "pending", "completing", "finalizing"].includes(it.status),
  );
  const allDone =
    hasSubmitted &&
    queue.length > 0 &&
    queue.every((it) => ["done", "failed", "cancelled"].includes(it.status));
  const successCount = queue.filter((it) => it.status === "done").length;
  const allowCustomSlug = queue.length === 1 && !hasSubmitted;

  // ──────────────── Stuck-upload watchdog + safety-net ────────────────
  // If an upload has been in `uploading` / `initializing` / `completing`
  // without ANY progress update for 60s, we consider it stuck (network hang,
  // paused sidecar tab, backend timeout that never surfaced etc.). Flip it
  // to `failed` and kick the scheduler so the queue keeps moving instead
  // of leaving the whole batch blocked behind the frozen item.
  const STUCK_TIMEOUT_MS = 60_000;
  useEffect(() => {
    const t = setInterval(() => {
      const q = queueRef.current || [];
      const now = Date.now();
      let unstuckAny = false;
      for (const it of q) {
        if (!["uploading", "initializing", "completing", "finalizing"].includes(it.status)) continue;
        const last = it.lastProgressAt || 0;
        if (last && now - last > STUCK_TIMEOUT_MS) {
          console.warn(`[upload] item ${it.id} stuck (no progress for ${now - last}ms)`);
          try { abortMapRef.current.get(it.id)?.abort(); } catch { /* noop */ }
          abortMapRef.current.delete(it.id);
          pauseMapRef.current.delete(it.id);
          updateItem(it.id, {
            status: "failed",
            errorMessage: "Kein Fortschritt seit 60 Sek. — Upload abgebrochen. Klick auf ↻ um erneut zu versuchen.",
          });
          unstuckAny = true;
        }
      }
      if (unstuckAny) safeRunSchedulerDeferred();
    }, 5_000);
    return () => clearInterval(t);
  }, [updateItem, safeRunSchedulerDeferred]);

  // ──────────────── beforeunload guard ────────────────
  // Warn the user before leaving the page while uploads are running or
  // still pending. Standard browser dialog — the custom text is ignored
  // by modern browsers, but the confirmation prompt is what matters.
  const hasPendingWork = queue.some((it) =>
    ["uploading", "initializing", "completing", "finalizing", "pending", "queued", "paused"].includes(it.status),
  );
  useEffect(() => {
    if (!hasPendingWork) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Uploads laufen noch. Wenn du jetzt neu lädst, gehen die verloren.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPendingWork]);

  // ──────────────── SPA Route-Guard (react-router useBlocker) ────────────────
  // The beforeunload handler above ONLY covers tab-close / hard-reload. It does
  // NOT fire on internal SPA navigation (clicking "Bibliothek", "Editor" etc.
  // in the Topbar). Without this guard, unmounting `AdminUpload` mid-upload
  // silently orphans the in-flight fetches: their `.then()` callbacks land on
  // an unmounted component (no-op setState), `completeUpload` never fires, and
  // the R2 multipart session is left hanging. Same UX contract as the
  // beforeunload guard: confirm first, kill AbortControllers cleanly on OK.
  const abortAllInFlight = useCallback(() => {
    for (const [, ctrl] of abortMapRef.current.entries()) {
      try { ctrl.abort(); } catch { /* noop */ }
    }
    abortMapRef.current.clear();
    pauseMapRef.current.clear();
  }, []);

  const routeBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasPendingWork && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (routeBlocker.state !== "blocked") return;
    const ok = window.confirm(
      "Uploads laufen noch. Beim Verlassen der Seite werden diese abgebrochen. Trotzdem verlassen?",
    );
    if (ok) {
      abortAllInFlight();
      routeBlocker.proceed();
    } else {
      routeBlocker.reset();
    }
  }, [routeBlocker, abortAllInFlight]);

  // ──────────────── Emergency reset ────────────────
  // If the scheduler ever hangs (state got wedged, browser tab throttled etc.)
  // this button lets the user recover without a full page reload — flips every
  // non-done item back to `pending` and re-runs the scheduler.
  const resetStuckQueue = useCallback(() => {
    for (const it of queueRef.current || []) {
      try { abortMapRef.current.get(it.id)?.abort(); } catch { /* noop */ }
      abortMapRef.current.delete(it.id);
      pauseMapRef.current.delete(it.id);
    }
    updateQueue((q) =>
      q.map((it) =>
        it.status === "done"
          ? it
          : { ...it, status: "pending", progress: 0, errorMessage: "", autoPaused: false, lastProgressAt: null },
      ),
    );
    toast.info("Warteschlange zurückgesetzt — Uploads werden neu gestartet.");
    safeRunSchedulerDeferred();
  }, [updateQueue, safeRunSchedulerDeferred]);

  // Toast when batch finishes.
  const wasInFlightRef = useRef(false);
  useEffect(() => {
    if (wasInFlightRef.current && !inFlight && hasSubmitted) {
      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? "Video hochgeladen"
            : `${successCount} Videos hochgeladen`,
        );
      }
    }
    wasInFlightRef.current = inFlight;
  }, [inFlight, hasSubmitted, successCount]);

  const slugHint = useMemo(() => {
    if (!allowCustomSlug) return null;
    const cleaned = sanitizeSlug(slugInput);
    if (!slugInput.trim()) {
      return (
        <p className="text-xs text-[#6a6a73]" data-testid="upload-slug-hint">
          Leer lassen → automatisch generierter Kurz-Link.
        </p>
      );
    }
    if (!SLUG_RE.test(cleaned)) {
      return (
        <p className="text-xs text-amber-400" data-testid="upload-slug-error">
          Ungültig — nur a–z, 0–9, Bindestriche, 1–60 Zeichen.
        </p>
      );
    }
    return (
      <p className="text-xs text-[#9a9aa3]" data-testid="upload-slug-preview">
        Dein Link:{" "}
        <span className="text-white font-mono">
          {(typeof window !== "undefined" ? window.location.origin : "") + "/v/" + cleaned}
        </span>
      </p>
    );
  }, [slugInput, allowCustomSlug]);

  const queuedCount = queue.filter((it) => it.status === "queued").length;
  const submitLabel = queuedCount === 0
    ? "Hochladen"
    : queuedCount === 1
      ? "Video hochladen"
      : `${queuedCount} Videos hochladen`;

  // Discard orphan localStorage entries (user clicked "Alle verwerfen" in banner).
  const onDismissResumeBanner = async () => {
    const orphans = listResumableFromLS();
    for (const { entry } of orphans) {
      if (entry.uploadId) {
        try {
          await fetch(`${API_BASE}/uploads/${encodeURIComponent(entry.uploadId)}`, {
            method: "DELETE",
            credentials: "include",
          });
        } catch {
          /* best effort */
        }
      }
    }
    try {
      for (const k of Object.keys(window.localStorage)) {
        if (k.startsWith(LS_PREFIX)) window.localStorage.removeItem(k);
      }
    } catch {
      /* noop */
    }
    setPendingUploads([]);
    setOrphanCount(0);
  };

  // "Alle Dateien erneut auswählen" — opens the same hidden file input.
  const onPickPendingFiles = () => {
    if (!inputRef.current) return;
    inputRef.current.click();
  };

  // ──────────────── Render ────────────────
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Topbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-[#9a9aa3] hover:text-white transition-colors mb-6"
          data-testid="upload-back-link"
        >
          <ArrowLeft size={14} />
          Zurück zur Bibliothek
        </Link>
        <div className="flex items-start gap-3 mb-1.5 flex-wrap">
          <h1 className="text-3xl font-bold tracking-tight">Video hochladen</h1>
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md border ${
              online
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-red-500/10 text-red-300 border-red-500/30"
            }`}
            data-testid="upload-connectivity-badge"
          >
            {online ? <Wifi size={11} /> : <WifiOff size={11} />}
            {online ? "Online" : "Offline"}
          </span>
        </div>
        <p className="text-sm text-[#9a9aa3] mt-1.5">
          Equirectangulare MP4-Dateien (2:1), max. 5 GB pro Datei. Resume-Upload — pause/resume und auto-resume nach Verbindungsabbruch.
        </p>

        {/* Resume banner — shows list of pending uploads + one-click re-pick */}
        {pendingUploads.length > 0 && (
          <div
            className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3.5"
            data-testid="upload-resume-banner"
          >
            <div className="flex items-start gap-3">
              <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/20 text-amber-300 shrink-0">
                <Play size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-100">
                  {pendingUploads.length === 1
                    ? "1 unterbrochener Upload vom letzten Mal"
                    : `${pendingUploads.length} unterbrochene Uploads vom letzten Mal`}
                </p>
                <p className="text-xs text-amber-200/80 mt-0.5">
                  Wähle dieselben Dateien hier nochmal — wir erkennen sie automatisch und setzen jeden Upload an der richtigen Stelle fort.
                </p>
              </div>
            </div>
            <ul
              className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-amber-500/20 bg-black/20 divide-y divide-amber-500/15"
              data-testid="upload-resume-banner-list"
            >
              {pendingUploads.map((p) => (
                <li
                  key={p.fingerprint}
                  className="flex items-center gap-3 px-3 py-2 text-xs"
                  data-testid={`upload-resume-banner-item-${p.fingerprint}`}
                >
                  <Film size={12} className="text-amber-300/70 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-amber-50 font-medium" title={p.fileName}>
                    {p.fileName}
                  </span>
                  <span className="text-amber-200/70 tabular-nums shrink-0">
                    {fmtSize(p.fileSize)}
                  </span>
                  <span className="text-amber-300 tabular-nums w-12 text-right shrink-0">
                    {p.progressPct}%
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onPickPendingFiles}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-amber-950 text-sm font-semibold transition-colors"
                data-testid="upload-resume-banner-repick"
              >
                <UploadCloud size={14} />
                Alle Dateien erneut auswählen
              </button>
              <button
                type="button"
                onClick={onDismissResumeBanner}
                className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-semibold transition-colors"
                data-testid="upload-resume-banner-dismiss"
              >
                Alle verwerfen
              </button>
              <span className="text-[11px] text-amber-300/70 self-center">
                Tipp: im Datei-Dialog Strg+A (alle) oder Shift-Klick (Bereich) drücken.
              </span>
            </div>
          </div>
        )}

        <div className="mt-8">
          <ResolutionTip />

          <form onSubmit={onSubmit} className="space-y-5" data-testid="upload-form">
            <div
              onDragOver={(e) => {
                if (inFlight) return;
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                if (inFlight) return;
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => {
                if (inFlight) return;
                inputRef.current?.click();
              }}
              className={`relative rounded-2xl border-2 border-dashed p-8 sm:p-10 text-center transition-all ${
                inFlight
                  ? "cursor-default border-[#2a2a30] bg-[#17171a]/40 opacity-70"
                  : dragOver
                    ? "cursor-pointer border-[#ff6b35] bg-[#ff6b35]/5"
                    : "cursor-pointer border-[#2a2a30] hover:border-[#3a3a40] bg-[#17171a]/40"
              }`}
              data-testid="upload-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept="video/mp4"
                multiple
                className="sr-only"
                onChange={onPickFiles}
                disabled={inFlight}
                data-testid="upload-file-input"
              />
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#1f1f23] mb-4">
                <UploadCloud size={24} className="text-[#9a9aa3]" />
              </div>
              <h3 className="text-base font-semibold">
                {queue.length === 0
                  ? "Datei(en) hier ablegen oder klicken"
                  : "Mehr Dateien hinzufügen"}
              </h3>
              <p className="text-sm text-[#9a9aa3] mt-1.5">
                MP4-Videos · max. 5 GB pro Datei · bis zu {MAX_FILES} gleichzeitig
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
                Sichtbarkeit{" "}
                {queue.length > 1 && (
                  <span className="text-[#5a5a60] normal-case font-normal">
                    (gilt für alle {queue.length} Dateien)
                  </span>
                )}
              </label>
              <div
                className={`grid grid-cols-2 gap-2 ${inFlight ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="upload-visibility-segmented"
              >
                <button
                  type="button"
                  onClick={() => setVisibility("public")}
                  disabled={inFlight}
                  className={`inline-flex items-center justify-center gap-2 h-11 rounded-lg border text-sm font-medium transition-colors ${
                    visibility === "public"
                      ? "bg-[#1f1f23] border-[#ff6b35] text-white"
                      : "bg-[#17171a] border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40]"
                  }`}
                  data-testid="upload-visibility-public"
                >
                  <Globe size={15} />
                  Öffentlich
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("private")}
                  disabled={inFlight}
                  className={`inline-flex items-center justify-center gap-2 h-11 rounded-lg border text-sm font-medium transition-colors ${
                    visibility === "private"
                      ? "bg-[#1f1f23] border-[#ff6b35] text-white"
                      : "bg-[#17171a] border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40]"
                  }`}
                  data-testid="upload-visibility-private"
                >
                  <Lock size={15} />
                  Privat
                </button>
              </div>
            </div>

            {allowCustomSlug && (
              <div className="space-y-1.5" data-testid="upload-slug-block">
                <label
                  htmlFor="slug"
                  className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider"
                >
                  Link-Name (optional)
                </label>
                <div className="flex items-stretch rounded-lg bg-[#17171a] border border-[#2a2a30] focus-within:border-[#ff6b35] focus-within:ring-2 focus-within:ring-[#ff6b35]/20 transition-colors overflow-hidden">
                  <span className="inline-flex items-center px-3 text-sm text-[#6a6a73] font-mono border-r border-[#2a2a30] select-none">
                    /v/
                  </span>
                  <input
                    id="slug"
                    value={slugInput}
                    onChange={(e) => setSlugInput(e.target.value)}
                    placeholder="strand-bali-2025"
                    maxLength={80}
                    autoComplete="off"
                    spellCheck={false}
                    className="flex-1 h-11 px-3 bg-transparent outline-none text-white placeholder:text-[#6a6a73] font-mono text-sm"
                    data-testid="upload-slug-input"
                  />
                </div>
                {slugHint}
              </div>
            )}
            {queue.length > 1 && (
              <div
                className="text-xs text-[#9a9aa3] bg-[#0d0d0e] border border-[#2a2a30] rounded-lg px-3.5 py-2.5"
                data-testid="upload-slug-multi-hint"
              >
                Bei Mehrfach-Upload werden automatische Link-Namen vergeben. Möchtest du eigene Links? Lade jede Datei einzeln hoch.
              </div>
            )}

            {queue.length > 0 && (
              <div className="space-y-2" data-testid="upload-queue">
                {queue.map((item) => (
                  <QueueRow
                    key={item.id}
                    item={item}
                    allowCustomSlug={allowCustomSlug}
                    onTitleChange={onTitleChange}
                    onPause={onPauseItem}
                    onResume={onResumeItem}
                    onCancel={onCancelItem}
                    onRemove={onRemoveItem}
                  />
                ))}
              </div>
            )}

            {globalError && (
              <div
                className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
                data-testid="upload-error"
              >
                {globalError}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              {allDone ? (
                <>
                  <span
                    className="inline-flex items-center gap-2 text-sm text-emerald-300 font-medium"
                    data-testid="upload-batch-summary"
                  >
                    <CheckCircle2 size={16} />
                    {successCount === queue.length
                      ? `${successCount} ${successCount === 1 ? "Video" : "Videos"} fertig`
                      : `${successCount}/${queue.length} erfolgreich`}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate("/admin")}
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)] hover:shadow-[0_10px_30px_rgba(255,75,80,0.45)] transition-shadow"
                    data-testid="upload-go-library-button"
                  >
                    Zur Bibliothek
                  </button>
                </>
              ) : (
                <button
                  type="submit"
                  disabled={queuedCount === 0 || inFlight}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)] hover:shadow-[0_10px_30px_rgba(255,75,80,0.45)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                  data-testid="upload-submit-button"
                >
                  <UploadCloud size={16} />
                  {submitLabel}
                </button>
              )}
              {inFlight && (
                <>
                  <span
                    className="text-xs text-[#9a9aa3] inline-flex items-center gap-1.5"
                    data-testid="upload-in-flight-hint"
                  >
                    <Loader2 size={12} className="animate-spin" />
                    Dateien werden sequenziell hochgeladen — kannst pausieren und fortsetzen.
                  </span>
                  <button
                    type="button"
                    onClick={resetStuckQueue}
                    className="text-xs text-[#6a6a73] hover:text-amber-300 underline underline-offset-2 transition-colors"
                    data-testid="upload-reset-queue-btn"
                    title="Warteschlange zurücksetzen (falls sie hängt)"
                  >
                    Warteschlange zurücksetzen
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
